/**
 * Purchase Order Management Hooks
 * 
 * Provides comprehensive CRUD operations for purchase orders in the procurement workflow.
 * Purchase orders are generated after a bid is awarded and milestones are approved by both parties.
 * 
 * @module usePurchaseOrders
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { PurchaseOrder, POModificationRequest, POStatus, POModificationRequestType } from '@/lib/types/job';
import { useToast } from '@/hooks/use-toast';
import { errorTracking } from '@/lib/errorTracking';

// Re-export the new simplified hook
export { useCreatePurchaseOrder } from './useCreatePurchaseOrder';

interface POIssuanceCheckResult {
  can_issue: boolean;
  reason?: string;
  payment_status?: string;
  payment_method_type?: string;
  ach_settlement_expected_at?: string;
}

/**
 * Options for filtering purchase orders
 */
interface UsePurchaseOrdersOptions {
  /** Filter by purchase order status */
  status?: string;
  /** Search by PO number or vendor name */
  search?: string;
}

/**
 * Hook for fetching and filtering purchase orders
 * 
 * Retrieves a list of purchase orders with optional filtering by status and search term.
 * The search functionality matches against PO number and vendor name.
 * 
 * @param options - Optional filters for status and search
 * @returns Object containing purchase orders array, loading state, error message, and refetch function
 * 
 * @example
 * ```tsx
 * function PurchaseOrdersList() {
 *   const { purchaseOrders, loading, error, refetch } = usePurchaseOrders({
 *     status: 'active',
 *     search: 'ACME'
 *   });
 *   
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *   
 *   return (
 *     <div>
 *       {purchaseOrders.map(po => (
 *         <POCard key={po.id} purchaseOrder={po} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const usePurchaseOrders = (options: UsePurchaseOrdersOptions = {}) => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('purchase_orders')
        .select(`
          *
        `)
        .order('created_at', { ascending: false });

      if (options.status) {
        query = query.eq('status', options.status);
      }

      if (options.search) {
        query = query.or(`po_number.ilike.%${options.search}%,vendor_name.ilike.%${options.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPurchaseOrders(data as PurchaseOrder[] || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
  }, [options.status, options.search]);

  return {
    purchaseOrders,
    loading,
    error,
    refetch: fetchPurchaseOrders
  };
};

/**
 * Helper to find most recent timestamp
 */
const getMostRecentTimestamp = (timestamps: (string | null | undefined)[]): string | null => {
  const validTimestamps = timestamps.filter((t): t is string => !!t);
  if (validTimestamps.length === 0) return null;
  
  return validTimestamps.reduce((latest, current) => {
    return new Date(current) > new Date(latest) ? current : latest;
  });
};

/**
 * Hook for fetching detailed information about a single purchase order
 * 
 * Retrieves comprehensive purchase order data. Most data is embedded in the PO
 * at creation time (billing_address, vendor_address, line_items), so this hook
 * primarily fetches live status information (milestones, reconciliation).
 * 
 * @param poId - The public ID of the purchase order to fetch
 * @returns Object containing purchase order data, loading state, error message, and refetch function
 * 
 * @example
 * ```tsx
 * function PurchaseOrderDetails({ poId }) {
 *   const { purchaseOrder, loading, error } = usePurchaseOrder(poId);
 *   
 *   if (!purchaseOrder) return <NotFound />;
 *   
 *   return (
 *     <div>
 *       <h1>PO {purchaseOrder.po_number}</h1>
 *       <p>Vendor: {purchaseOrder.vendor_company?.name}</p>
 *       <p>Amount: ${(purchaseOrder.po_amount_cents / 100).toFixed(2)}</p>
 *       <MilestoneTimeline milestones={purchaseOrder.milestones} />
 *     </div>
 *   );
 * }
 * ```
 */
export const usePurchaseOrder = (poId?: string) => {
  const [purchaseOrder, setPurchaseOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPurchaseOrder = async () => {
    if (!poId) {
      setError('No purchase order ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch the purchase order - most data is embedded at creation
      const { data: poData, error: poError } = await supabase
        .from('purchase_orders')
        .select(`
          *,
          jobs!purchase_orders_job_id_fkey (
            id,
            public_id,
            title,
            description,
            status,
            organization_id,
            created_by_user_id,
            created_at
          )
        `)
        .eq('public_id', poId)
        .maybeSingle();

      if (poError) throw poError;
      if (!poData) {
        setError('Purchase order not found');
        setLoading(false);
        return;
      }

      console.log('Purchase order data:', poData);

      // Fetch live status information
      
      // 1. Milestones (for progress tracking)
      const { data: milestones, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .eq('job_id', poData.job_id)
        .order('milestone_order', { ascending: true });

      if (milestonesError) {
        console.error('Error fetching milestones:', milestonesError);
      }

      // 2. Delivery details (may change during execution)
      const { data: deliveryDetails, error: deliveryError } = await supabase
        .from('job_delivery_details')
        .select('*')
        .eq('job_id', poData.job_id)
        .order('delivery_order', { ascending: true });

      if (deliveryError) {
        console.error('Error fetching delivery details:', deliveryError);
      }

      // 3. Reconciliation status (live financial status)
      const { data: reconciliation, error: reconciliationError } = await supabase
        .from('job_financial_reconciliations')
        .select('*')
        .eq('purchase_order_id', poData.id)
        .maybeSingle();

      if (reconciliationError) {
        console.error('Error fetching reconciliation:', reconciliationError);
      }

      // 4. Quantity distribution by location (show all - display percentages if not finalized)
      const { data: quantityDistribution, error: quantityDistError } = await supabase
        .from('job_specification_location_quantities')
        .select('*, job_specifications!inner(specification_name)')
        .eq('job_id', poData.job_id);

      if (quantityDistError) {
        console.error('Error fetching quantity distribution:', quantityDistError);
      }

      // 4. Bid details (for reference)
      let bidDetails = null;
      if (poData.winning_bid_id) {
        const { data: bid, error: bidError } = await supabase
          .from('bids')
          .select('*, rfqs!fk_bids_rfq_id(*)')
          .eq('id', poData.winning_bid_id)
          .single();

        if (!bidError && bid) {
          bidDetails = bid;
        }
      }

      // Compute the latest activity timestamp
      const latestActivityAt = getMostRecentTimestamp([
        poData.created_at,
        poData.sent_to_vendor_at,
        poData.approved_at,
        poData.updated_at
      ]);

      // Use embedded data from PO (immutable snapshots)
      // Restructure vendor_company to match display component expectations
      const vendorAddr = poData.vendor_address as any;
      const vendor_company = vendorAddr ? {
        id: poData.vendor_organization_id,
        name: vendorAddr.name || 'Unknown Vendor',
        address: {
          street_address: vendorAddr.street_address,
          city: vendorAddr.city,
          state: vendorAddr.state,
          state_province: vendorAddr.state, // Alias for compatibility
          zip_code: vendorAddr.zip_code,
          postal_code: vendorAddr.zip_code, // Alias for compatibility
          country: vendorAddr.country
        },
        person: {
          first_name: vendorAddr.contact_name?.split(' ')[0] || null,
          last_name: vendorAddr.contact_name?.split(' ').slice(1).join(' ') || null,
          job_title: vendorAddr.contact_title
        },
        contact_email: vendorAddr.contact_email
      } : {
        id: poData.vendor_organization_id,
        name: 'Unknown Vendor',
        address: null,
        person: null,
        contact_email: null
      };

      // Restructure client_company to match display component expectations
      const billingAddr = poData.billing_address as any;
      const client_company = billingAddr ? {
        id: poData.organization_id,
        name: billingAddr.name || 'Unknown Client',
        address: {
          street_address: billingAddr.street_address,
          city: billingAddr.city,
          state: billingAddr.state,
          state_province: billingAddr.state, // Alias for compatibility
          zip_code: billingAddr.zip_code,
          postal_code: billingAddr.zip_code, // Alias for compatibility
          country: billingAddr.country
        },
        person: {
          first_name: billingAddr.contact_name?.split(' ')[0] || null,
          last_name: billingAddr.contact_name?.split(' ').slice(1).join(' ') || null,
          job_title: billingAddr.contact_title
        },
        contact_email: billingAddr.contact_email
      } : {
        id: poData.organization_id,
        name: 'Unknown Client',
        address: null,
        person: null,
        contact_email: null
      };

      const line_items = poData.line_items || [];

      // Build the enriched purchase order object
      const enrichedPO: any = {
        ...poData,
        job: poData.jobs,
        bid: bidDetails,
        milestones: milestones || [],
        line_items,
        delivery_locations: deliveryDetails || [],
        quantity_distribution: quantityDistribution || [],
        vendor_company,
        client_company,
        reconciliation_status: reconciliation?.reconciliation_status || null,
        latest_activity_at: latestActivityAt
      };

      setPurchaseOrder(enrichedPO);
    } catch (err) {
      console.error('Error in usePurchaseOrder:', err);
      errorTracking.captureError(err as Error, {
        context: 'usePurchaseOrder',
        poId
      });
      setError(err instanceof Error ? err.message : 'Failed to fetch purchase order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrder();
  }, [poId]);

  return { purchaseOrder, loading, error, refetch: fetchPurchaseOrder };
};

/**
 * Hook for creating new purchase orders
 * 
 * Handles the complex process of generating a purchase order from an awarded bid:
 * 1. Validates milestone approval status (both parties must approve)
 * 2. Fetches job, specifications, delivery details, and bid information
 * 3. Generates unique PO number based on organization and year
 * 4. Calculates total amount from award selections
 * 5. Creates line items for each specification
 * 6. Optionally includes milestones and locks them
 * 7. Sends notification to vendor
 * 
 * The PO is created in 'draft' status and must be accepted by the vendor before
 * the job status advances to 'in_progress'.
 * 
 * @returns Object with createPurchaseOrder function and loading state
 * 
 * @example
 * ```tsx
 * function CreatePOButton({ jobId, winningBidId }) {
 *   const { createPurchaseOrder, loading } = useCreatePurchaseOrder();
 *   
 *   const handleCreate = async () => {
 *     try {
 *       const po = await createPurchaseOrder(jobId, winningBidId, true);
 *       console.log('Created PO:', po.po_number);
 *     } catch (error) {
 *       console.error('Failed to create PO:', error);
 *     }
 *   };
 *   
 *   return (
 *     <button onClick={handleCreate} disabled={loading}>
 *       {loading ? 'Creating...' : 'Create Purchase Order'}
 *     </button>
 *   );
 * }
 * ```
 */
/**
 * Pre-flight validation to check all prerequisites for PO creation
 */
const validatePOPrerequisites = async (jobId: string, winningBidId: string) => {
  const validationResults = {
    paymentSetup: false,
    milestonesApproved: false,
    paymentStatusCorrect: false,
    errors: [] as string[]
  };

  try {
    // Check payment setup
    const { data: setupStatus } = await supabase
      .from('payment_setup_status')
      .select('*')
      .eq('job_id', jobId)
      .single();

    validationResults.paymentSetup = setupStatus?.both_complete === true;
    if (!validationResults.paymentSetup) {
      validationResults.errors.push(`Payment setup incomplete: client=${setupStatus?.client_setup_complete}, vendor=${setupStatus?.vendor_setup_complete}`);
    }

    // Check milestone approval
    const { data: approval } = await supabase
      .from('milestone_approvals')
      .select('*')
      .eq('job_id', jobId)
      .single();

    validationResults.milestonesApproved = approval?.approval_status === 'approved_by_both';
    if (!validationResults.milestonesApproved) {
      validationResults.errors.push(`Milestones not approved: status=${approval?.approval_status}`);
    }

    // Check job payment status
    const { data: job } = await supabase
      .from('jobs')
      .select('payment_status, public_id, status')
      .eq('id', jobId)
      .single();

    validationResults.paymentStatusCorrect = job?.payment_status !== 'not_required';
    if (!validationResults.paymentStatusCorrect) {
      validationResults.errors.push(`Job payment_status is '${job?.payment_status}' but payment setup is complete`);
    }

    // Note: Vendor organization validation is handled server-side by the edge function
    // which has service_role access to query across organizations.
    // Client-side validation fails due to RLS preventing cross-org profile access.

    console.log('[PO Validation]', validationResults);
    return validationResults;
  } catch (err) {
    console.error('[PO Validation] Error during validation:', err);
    validationResults.errors.push(`Validation error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    return validationResults;
  }
};


/**
 * Hook for fetching modification requests for a purchase order
 * 
 * Retrieves all modification requests associated with a PO, ordered by creation date
 * (most recent first). Modification requests track changes requested by either party
 * after the PO has been issued.
 * 
 * @param poId - The UUID of the purchase order
 * @returns Object containing modifications array, loading state, and error message
 * 
 * @example
 * ```tsx
 * function POModificationHistory({ poId }) {
 *   const { modifications, loading } = usePOModifications(poId);
 *   
 *   return (
 *     <div>
 *       <h3>Modification History</h3>
 *       {modifications.map(mod => (
 *         <div key={mod.id}>
 *           <p>{mod.modification_type}: {mod.modification_status}</p>
 *           <p>{mod.reason}</p>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const usePOModifications = (poId?: string) => {
  const [modifications, setModifications] = useState<POModificationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poId) return;

    const fetchModifications = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('po_modification_requests')
          .select('*')
          .eq('purchase_order_id', poId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setModifications(data as POModificationRequest[] || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch modifications');
      } finally {
        setLoading(false);
      }
    };

    fetchModifications();
  }, [poId]);

  return { modifications, loading, error };
};